import { forwardRef } from 'react'
import { Control, FieldPathByValue, FieldPathValue, FieldValues, UseControllerProps, useController, useWatch } from 'react-hook-form'
import { NumberInputBase, NumberInputProps } from '../NumberInput/NumberInput'
import { isRequiredRule } from '../TextInput/TextInput'

export type PercentageInputProps = Omit<NumberInputProps, 'defaultValue' | 'format' | 'value'> & {
    defaultValue?: number
    value?: number
}

export const PercentageInput = forwardRef<HTMLInputElement, PercentageInputProps>(function PercentageInput(props, ref) {
    const { defaultValue, onValueChange, unit = '%', value, ...numberInputProps } = props
    const isControlled = Object.prototype.hasOwnProperty.call(props, 'value')
    const controlledValue = isControlled ? { value: toDisplayedPercentage(value) } : {}

    return (
        <NumberInputBase
            {...numberInputProps}
            {...controlledValue}
            areValuesEquivalent={arePercentageValuesEquivalent}
            defaultValue={toDisplayedPercentage(defaultValue)}
            format="decimal"
            inputRef={ref}
            maximumFractionDigits={5}
            onValueChange={(nextValue) => {
                onValueChange?.(toStoredPercentage(nextValue))
            }}
            unit={unit}
        />
    )
})

type PercentageFieldPath<TFieldValues extends FieldValues> = FieldPathByValue<TFieldValues, number | undefined>

type FormPercentageInputRules<TFieldValues extends FieldValues, TName extends PercentageFieldPath<TFieldValues>> = Omit<
    NonNullable<UseControllerProps<TFieldValues, TName>['rules']>,
    'maxLength' | 'minLength' | 'pattern'
>

export type FormPercentageInputProps<
    TFieldValues extends FieldValues,
    TName extends PercentageFieldPath<TFieldValues> = PercentageFieldPath<TFieldValues>,
> = Omit<PercentageInputProps, 'defaultValue' | 'errorMessage' | 'name' | 'onValueChange' | 'required' | 'value'> & {
    control: Control<TFieldValues>
    name: TName
    rules?: FormPercentageInputRules<TFieldValues, TName>
}

export function FormPercentageInput<
    TFieldValues extends FieldValues,
    TName extends PercentageFieldPath<TFieldValues> = PercentageFieldPath<TFieldValues>,
>({ control, name, onBlur, rules, ...percentageInputProps }: FormPercentageInputProps<TFieldValues, TName>) {
    const { validate, ...otherRules } = rules ?? {}
    const { field, fieldState } = useController({
        control,
        name,
        rules: {
            ...otherRules,
            validate:
                validate ??
                ((fieldValue: FieldPathValue<TFieldValues, TName>) => {
                    if (fieldValue === undefined) {
                        return true
                    }
                    if (typeof fieldValue !== 'number' || Number.isNaN(fieldValue)) {
                        return 'Bitte geben Sie eine Zahl ein'
                    }
                    return true
                }),
        },
    })
    const value = useWatch({ control, exact: true, name })

    return (
        <PercentageInput
            {...percentageInputProps}
            errorMessage={fieldState.error ? (fieldState.error.message?.toString() ?? '') : undefined}
            name={field.name}
            onBlur={(event) => {
                field.onBlur()
                onBlur?.(event)
            }}
            onValueChange={field.onChange}
            ref={field.ref}
            required={isRequiredRule(rules?.required)}
            value={value}
        />
    )
}

function toDisplayedPercentage(value: number | undefined) {
    return typeof value === 'number' ? Number((value * 100).toFixed(5)) : value
}

function toStoredPercentage(value: number | undefined) {
    return value === undefined ? undefined : Number((value / 100).toFixed(5))
}

function arePercentageValuesEquivalent(firstValue: number | undefined, secondValue: number | undefined) {
    return Object.is(toStoredPercentage(firstValue), toStoredPercentage(secondValue))
}
